/**
 * Size chart logic for LAA3.0
 *
 * Each .size-chart-modal (dialog-component) is independent.
 * On `dialog:open`, fetches WP API data using `data-style-slug`.
 * If WP returns valid inch/cm table data → shows dynamic WP chart (trumps metafield).
 * If not → falls back to the metafield .size_chart HTML.
 */
(function () {
  // ── Unit conversion ───────────────────────────────────────────────────────────

  function parseInchValue(val) {
    val = val.trim();
    if (val.includes(' ')) {
      const parts = val.split(' ');
      const whole = parseFloat(parts[0]);
      const fracParts = parts[1].split('/');
      return fracParts.length === 2
        ? whole + parseFloat(fracParts[0]) / parseFloat(fracParts[1])
        : whole;
    }
    const mergedMatch = val.match(/^(\d+)(\d\/\d+)$/);
    if (mergedMatch) {
      const whole = parseFloat(mergedMatch[1]);
      const fracParts = mergedMatch[2].split('/');
      return fracParts.length === 2
        ? whole + parseFloat(fracParts[0]) / parseFloat(fracParts[1])
        : whole;
    }
    if (val.includes('/')) {
      const fracParts = val.split('/');
      return fracParts.length === 2
        ? parseFloat(fracParts[0]) / parseFloat(fracParts[1])
        : 0;
    }
    return parseFloat(val) || 0;
  }

  function convertToCm(inchStr) {
    return Math.ceil(parseInchValue(inchStr) * 2.54);
  }

  function formatInches(inchStr) {
    const trimmed = inchStr.trim();
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ');
      return `${parts[0]} <sup>${parts.slice(1).join(' ')}</sup>`;
    }
    const mergedMatch = trimmed.match(/^(\d+)(\d\/\d+)$/);
    if (mergedMatch) return `${mergedMatch[1]} <sup>${mergedMatch[2]}</sup>`;
    if (/^\d+\/\d+$/.test(trimmed)) return `<sup>${trimmed}</sup>`;
    return trimmed;
  }

  // ── Render helpers ────────────────────────────────────────────────────────────

  function renderTable(product, unit, modal) {
    const inchesData = product?.acf?.inches;
    if (!inchesData) return;

    const headerHtml = inchesData.use_header && inchesData.header?.length
      ? `<thead><tr>${inchesData.header.map(h =>
          `<th scope="col">${h.c}</th>`
        ).join('')}</tr></thead>`
      : '';

    const bodyHtml = `<tbody>${inchesData.body.map(row =>
      `<tr>${row.map((cell, i) => {
        let v = cell.c;
        if (i !== 0) v = unit === 'cm' ? convertToCm(v) : formatInches(v);
        return `<td>${v}</td>`;
      }).join('')}</tr>`
    ).join('')}</tbody>`;

    const target = modal.querySelector('[data-size-table]');
    if (target) {
      target.innerHTML = `<table class="table_size_guide">${headerHtml}${bodyHtml}</table>`;
    }
  }

  function renderImage(product, modal) {
    const holder = modal.querySelector('.size-chart-image-col');
    const figure = modal.querySelector('[data-model-image]');
    const description = modal.querySelector('[data-description-details]');

    const featured = product?.better_featured_image;
    const descText = product?.acf?.description_details || '';
    const hasImage = !!featured?.source_url;
    const hasDesc = !!descText;

    // Hide the entire column when there's neither an image nor a description
    if (!hasImage && !hasDesc) {
      if (holder) holder.hidden = true;
      return;
    }
    if (holder) holder.hidden = false;

    if (figure) {
      if (hasImage) {
        let srcset = '';
        const sizes = featured.media_details?.sizes;
        if (sizes) {
          srcset = Object.values(sizes)
            .filter(s => s.source_url && s.width)
            .map(s => `${s.source_url} ${s.width}w`)
            .join(', ');
        }
        figure.innerHTML = `<img src="${featured.source_url}" srcset="${srcset}" alt="${featured.alt_text || ''}" loading="lazy">`;
        figure.hidden = false;
      } else {
        figure.innerHTML = '';
        figure.hidden = true;
      }
    }

    if (description) {
      if (hasDesc) {
        description.innerHTML = descText;
        description.querySelectorAll('[style]').forEach(function(el){ el.removeAttribute('style'); });
        description.hidden = false;
      } else {
        description.innerHTML = '';
        description.hidden = true;
      }
    }
  }

  function getCurrentUnit(modal) {
    return modal.querySelector('input[name^="size-unit"]:checked')?.value || 'inches';
  }

  // ── Show / hide chart sections ────────────────────────────────────────────────

  function hideLoader(modal) {
    const loader = modal.querySelector('.size-chart-loader');
    if (loader) loader.hidden = true;
  }

  function showLoader(modal) {
    const loader = modal.querySelector('.size-chart-loader');
    const wpEl = modal.querySelector('.wp__size-guide');
    const metaEl = modal.querySelector('.size_chart');
    if (loader) loader.hidden = false;
    if (wpEl) wpEl.hidden = true;
    if (metaEl) metaEl.hidden = true;
  }

  function showMetafieldChart(modal) {
    const metaEl = modal.querySelector('.size_chart');
    const wpEl = modal.querySelector('.wp__size-guide');
    if (wpEl) wpEl.hidden = true;
    if (metaEl) metaEl.hidden = false;
    hideLoader(modal);
  }

  function showWpChart(modal, renderImg = true) {
    const metaEl = modal.querySelector('.size_chart');
    const wpEl = modal.querySelector('.wp__size-guide');
    if (metaEl) metaEl.hidden = true;
    if (wpEl) wpEl.hidden = false;
    renderTable(modal._wpProduct, getCurrentUnit(modal), modal);
    if (renderImg) renderImage(modal._wpProduct, modal);
    hideLoader(modal);
  }

  // ── Check whether WP API payload has usable inch/cm data ─────────────────────

  function hasValidWpData(product) {
    const inches = product?.acf?.inches;
    return inches && Array.isArray(inches.body) && inches.body.length > 0;
  }

  // ── Module-level cache keyed by slug ─────────────────────────────────────────
  // Survives variant-picker morphs that replace the modal element. Stores
  // either a product object (with usable inch data) or null (fetched but
  // no usable data — fall back to metafield).
  const wpCache = new Map();
  // In-flight fetches keyed by slug, so duplicate opens reuse one request.
  const wpPending = new Map();

  function fetchProduct(slug) {
    if (wpCache.has(slug)) return Promise.resolve(wpCache.get(slug));
    if (wpPending.has(slug)) return wpPending.get(slug);

    const url = `https://laapparel.info/wp-json/wp/v2/wholesale_product?slug=${encodeURIComponent(slug)}`;
    const promise = fetch(url)
      .then(r => r.json())
      .then(products => {
        const product = Array.isArray(products) && products.length ? products[0] : null;
        const value = product && hasValidWpData(product) ? product : null;
        wpCache.set(slug, value);
        wpPending.delete(slug);
        return value;
      })
      .catch(() => {
        wpCache.set(slug, null);
        wpPending.delete(slug);
        return null;
      });
    wpPending.set(slug, promise);
    return promise;
  }

  function applyChartState(modal, product) {
    if (product) {
      modal._wpProduct = product;
      showWpChart(modal);
    } else {
      modal._wpProduct = null;
      showMetafieldChart(modal);
    }
  }

  // ── Init per modal ────────────────────────────────────────────────────────────

  function initSizeChart(modal) {
    const slug = modal.dataset.styleSlug;

    if (!slug) {
      showMetafieldChart(modal);
      return;
    }

    fetchProduct(slug).then(product => applyChartState(modal, product));
  }

  // ── Per-modal dialog open handler ────────────────────────────────────────────
  // Shared by both per-element listeners and the document-level fallback.
  function handleDialogOpen(modal) {
    const slug = modal.dataset.styleSlug;
    if (!slug) {
      showMetafieldChart(modal);
      return;
    }
    // Cached → apply right away (no loader flash)
    if (wpCache.has(slug)) {
      applyChartState(modal, wpCache.get(slug));
      return;
    }
    // Not cached → show loader, fetch, then apply when ready
    showLoader(modal);
    initSizeChart(modal);
  }

  // ── Wire up all size chart modals ─────────────────────────────────────────────

  function setup(root) {
    root.querySelectorAll('.size-chart-modal').forEach(modal => {
      if (modal._sizeChartReady) return;
      modal._sizeChartReady = true;

      // If we already have cached data for this slug, apply it immediately so
      // there's no flash of loader/metafield content when the modal opens.
      const slug = modal.dataset.styleSlug;
      if (slug && wpCache.has(slug)) {
        applyChartState(modal, wpCache.get(slug));
      }

      modal.addEventListener('dialog:open', () => handleDialogOpen(modal));

      // Unit toggle: re-render table only (do NOT touch visibility)
      // stopPropagation prevents variant-picker from treating the radio change as a variant selection
      modal.addEventListener('change', (event) => {
        if (!event.target.matches('input[name^="size-unit"]')) return;
        event.stopPropagation();
        const wpEl = modal.querySelector('.wp__size-guide');
        if (modal._wpProduct && wpEl && !wpEl.hidden) {
          renderTable(modal._wpProduct, getCurrentUnit(modal), modal);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup(document));
  } else {
    setup(document);
  }

  document.addEventListener('shopify:section:load', e => setup(e.target));

  // Document-level fallback dialog:open listener.
  // If a variant-picker morph replaces the modal element entirely, the new
  // element loses its per-element listener. This document-level listener
  // (in capture phase, so it fires before the bubble would otherwise hit
  // a missing listener) catches dialog:open from any size-chart-modal —
  // including freshly-morphed ones — and applies the cached state.
  document.addEventListener(
    'dialog:open',
    (event) => {
      const target = event.target;
      if (!target || target.nodeType !== 1) return;
      const modal = target.closest?.('.size-chart-modal');
      if (!modal) return;
      // Make sure the modal has its listeners wired so the unit toggle still works
      if (!modal._sizeChartReady) {
        setup(modal.parentElement || document);
      }
      handleDialogOpen(modal);
    },
    true
  );
})();
